import { Instance } from "@/types";
import { useInvalidate, UseInvalidateProp, useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";

export type MutateInstanceParams = {
  action: 'start' | 'stop' | 'restart' | 'fetch' | 'delete' | 'update';
  id: string;
  values?: Instance;
};
/**
 * Custom hook for managing instances.
 * 
 * @param props - The hook props.
 *   - invalidate: The invalidate function from the `useInvalidate` hook.
 * @returns An object containing the `mutate` function and `isLoading` flag.
 */

export const useInstanceUpdate = (props: { invalidate?: UseInvalidateProp, resource?:string} = {}) => {
  const { t } = useTranslation();
  const invalidate = useInvalidate();
  const { mutate: originalMutate, isLoading } = useUpdate<Instance>({
    successNotification: (response, { id, values }, resource) => {
      const { action, data } = values as any;
      const { invalidate: invalidateFromProps, resource: invalidateResource  } = props;
      invalidate({
        resource: invalidateResource,
        invalidates: ["list"],
        ...invalidateFromProps,
      });
      return {
        message: t(`instances.actionResult.${action}.success`, { id }),
        description: "Success",
        type: "success",
      };
    },
    errorNotification: (error, { id, values }, resource) => {
      const { action } = values as any;
      return {
        message: t(`instances.actionResult.${action}.failed`, { id }),
        type: "error"
      };
    },
  });
  /**
   * Mutates an instance with the given parameters.
   * 
   * @param variables - The variables for the mutation.
   *   - action: The action to perform on the instance. Possible values are 'start', 'stop', 'restart', 'fetch', 'delete', and 'update'.
   *   - id: The ID of the instance.
   *   - values: Optional. The updated values for the instance.
   * @returns A promise that resolves to the result of the mutation.
   */
  const mutate = async (variables: MutateInstanceParams) => {
    const { action, values: data, ...rest } = variables;
    return await originalMutate({
      resource: "instances",
      values: {
        action,
        data,
      },
      ...rest,
    });
  };
  return { mutate, isLoading };
};